import { useEffect, useMemo, useState } from 'react';
import {
  Search, UserPlus, Users, DollarSign, Percent, Mail,
  Phone, Edit2, Check, X, UserX, MessageCircle, Eye, EyeOff,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { userService } from '../../services/userService';
import { UI, buttonStyle, fieldStyles, ui } from '../../utils/ui';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const statusPill = (active) => ({
  display: 'inline-flex', alignItems: 'center', padding: '4px 10px',
  borderRadius: 9999, fontSize: 11, fontWeight: 900, whiteSpace: 'nowrap',
  border: `1px solid ${active ? '#86efac' : '#fecaca'}`,
  backgroundColor: active ? '#dcfce7' : '#fee2e2',
  color: active ? '#166534' : '#991b1b',
});

const inputRowStyle = {
  display: 'flex', alignItems: 'center', gap: 10,
  padding: '10px 14px', borderRadius: 14,
  border: `1.5px solid ${UI.colors.border}`, backgroundColor: '#ffffff',
};
const inputCore = {
  border: 'none', outline: 'none', flex: 1, fontSize: 14,
  backgroundColor: 'transparent', color: UI.colors.text,
};

// ─── Create Agent Modal ───────────────────────────────────────────────────────

const CreateAgentModal = ({ onClose, onCreated }) => {
  const [form, setForm] = useState({ name: '', email: '', phone: '', password: '', commission_rate: '10' });
  const [showPwd, setShowPwd] = useState(false);
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.password.trim()) {
      return toast.error('Name, email and password are required.');
    }
    const rate = parseFloat(form.commission_rate);
    if (isNaN(rate) || rate < 0 || rate > 100) {
      return toast.error('Commission rate must be between 0 and 100.');
    }
    setSaving(true);
    try {
      const res = await userService.createAgent({
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || undefined,
        password: form.password,
        commission_rate: rate,
      });
      toast.success(`Agent "${res.data.agent.name}" created!`);
      onCreated(res.data.agent);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create agent.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)' }} onClick={onClose} />
      <div style={{ ...ui.card, position: 'relative', width: '100%', maxWidth: 480, zIndex: 1, padding: 0, overflow: 'hidden' }}>
        {/* Header */}
        <div style={{ padding: '20px 24px 16px', borderBottom: `1px solid ${UI.colors.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <h2 style={{ fontFamily: UI.font.heading, fontSize: 22, fontWeight: 900, margin: 0, color: UI.colors.text }}>Create Agent</h2>
            <p style={{ margin: '4px 0 0', fontSize: 13, color: UI.colors.muted }}>Add a new agent to the system</p>
          </div>
          <button onClick={onClose} style={{ border: 'none', background: 'none', cursor: 'pointer', padding: 6, borderRadius: 8, color: UI.colors.muted, display: 'flex' }}>
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} style={{ padding: '20px 24px 24px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          {/* Name */}
          <div>
            <label style={fieldStyles.label}>Full Name *</label>
            <div className="input-row" style={inputRowStyle}>
              <Users size={16} color={form.name ? UI.colors.primary : '#9ca3af'} />
              <input type="text" placeholder="Agent's full name" value={form.name} onChange={(e) => set('name', e.target.value)} style={inputCore} required />
            </div>
          </div>

          {/* Email */}
          <div>
            <label style={fieldStyles.label}>Email Address *</label>
            <div className="input-row" style={inputRowStyle}>
              <Mail size={16} color={form.email ? UI.colors.primary : '#9ca3af'} />
              <input type="email" placeholder="agent@email.com" value={form.email} onChange={(e) => set('email', e.target.value)} style={inputCore} required />
            </div>
          </div>

          {/* Phone */}
          <div>
            <label style={fieldStyles.label}>Phone Number</label>
            <div className="input-row" style={inputRowStyle}>
              <Phone size={16} color={form.phone ? UI.colors.primary : '#9ca3af'} />
              <input type="tel" placeholder="+91 98765 43210" value={form.phone} onChange={(e) => set('phone', e.target.value)} style={inputCore} />
            </div>
          </div>

          {/* Password */}
          <div>
            <label style={fieldStyles.label}>Password *</label>
            <div className="input-row" style={inputRowStyle}>
              <input
                type={showPwd ? 'text' : 'password'}
                placeholder="Set a password for the agent"
                value={form.password}
                onChange={(e) => set('password', e.target.value)}
                style={inputCore}
                required
              />
              <button type="button" onClick={() => setShowPwd((p) => !p)}
                style={{ border: 'none', background: 'none', cursor: 'pointer', padding: 2, color: '#9ca3af', display: 'flex', flexShrink: 0 }}>
                {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          {/* Commission Rate */}
          <div>
            <label style={fieldStyles.label}>Commission Rate (%)</label>
            <div className="input-row" style={inputRowStyle}>
              <Percent size={16} color={UI.colors.primary} />
              <input
                type="number"
                min="0"
                max="100"
                step="0.5"
                placeholder="10"
                value={form.commission_rate}
                onChange={(e) => set('commission_rate', e.target.value)}
                style={inputCore}
              />
            </div>
            <p style={{ margin: '6px 0 0', fontSize: 12, color: UI.colors.muted }}>Percentage of booking total the agent earns on each booking</p>
          </div>

          {/* WhatsApp notice */}
          <div style={{ padding: '10px 14px', borderRadius: 12, backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', display: 'flex', alignItems: 'center', gap: 10 }}>
            <MessageCircle size={16} color="#16a34a" />
            <p style={{ margin: 0, fontSize: 12, color: '#166534', fontWeight: 700 }}>
              WhatsApp credential delivery coming soon. Share login details manually for now.
            </p>
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', gap: 10, paddingTop: 4 }}>
            <button type="button" onClick={onClose} style={{ ...buttonStyle('outline'), flex: 1, padding: '12px 16px', borderRadius: 14 }}>
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              style={{ ...buttonStyle('primary'), flex: 1, padding: '12px 16px', borderRadius: 14, opacity: saving ? 0.75 : 1 }}
            >
              {saving ? (
                <span style={{ width: 16, height: 16, border: '2px solid rgba(255,255,255,0.35)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
              ) : (
                <UserPlus size={16} />
              )}
              {saving ? 'Creating...' : 'Create Agent'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─── Inline Commission Editor ─────────────────────────────────────────────────

const CommissionEditor = ({ agent, onSaved }) => {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(String(parseFloat(agent.commission_rate ?? 10)));
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    const rate = parseFloat(value);
    if (isNaN(rate) || rate < 0 || rate > 100) return toast.error('Rate must be 0–100.');
    setSaving(true);
    try {
      await userService.updateAgentCommission(agent.id, rate);
      onSaved(agent.id, rate);
      toast.success(`Commission updated to ${rate}%`);
      setEditing(false);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update commission.');
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontWeight: 900, color: '#16a34a', fontSize: 14 }}>
          {parseFloat(agent.commission_rate ?? 10).toFixed(1)}%
        </span>
        <button
          type="button"
          onClick={() => { setValue(String(parseFloat(agent.commission_rate ?? 10))); setEditing(true); }}
          style={{ border: 'none', background: 'none', cursor: 'pointer', padding: 4, borderRadius: 6, color: '#9ca3af', display: 'flex' }}
          title="Edit commission rate"
        >
          <Edit2 size={13} />
        </button>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <input
        type="number"
        min="0"
        max="100"
        step="0.5"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        autoFocus
        style={{
          width: 64, padding: '5px 8px', borderRadius: 8, border: `1.5px solid ${UI.colors.primary}`,
          fontSize: 13, fontWeight: 900, color: UI.colors.text, outline: 'none', textAlign: 'center',
        }}
      />
      <span style={{ fontSize: 12, color: UI.colors.muted, fontWeight: 700 }}>%</span>
      <button
        type="button"
        onClick={handleSave}
        disabled={saving}
        style={{ border: 'none', background: '#16a34a', cursor: 'pointer', borderRadius: 8, padding: '5px 8px', color: '#fff', display: 'flex', opacity: saving ? 0.7 : 1 }}
        title="Save"
      >
        {saving
          ? <span style={{ width: 12, height: 12, border: '2px solid rgba(255,255,255,0.4)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
          : <Check size={13} />}
      </button>
      <button
        type="button"
        onClick={() => setEditing(false)}
        style={{ border: 'none', background: '#f3f4f6', cursor: 'pointer', borderRadius: 8, padding: '5px 8px', color: '#6b7280', display: 'flex' }}
        title="Cancel"
      >
        <X size={13} />
      </button>
    </div>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────

const ManageAgents = () => {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [deactivating, setDeactivating] = useState(null);

  useEffect(() => {
    userService.getAgents()
      .then((res) => setAgents(res.data.users || []))
      .catch(() => toast.error('Failed to load agents.'))
      .finally(() => setLoading(false));
  }, []);

  const handleCommissionSaved = (agentId, newRate) => {
    setAgents((prev) => prev.map((a) => a.id === agentId ? { ...a, commission_rate: newRate } : a));
  };

  const doDeactivate = async (id, name) => {
    setDeactivating(id);
    try {
      await userService.deactivateUser(id);
      setAgents((prev) => prev.map((a) => a.id === id ? { ...a, is_active: false } : a));
      toast.success(`${name}'s account deactivated.`);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to deactivate agent.');
    } finally {
      setDeactivating(null);
    }
  };

  const handleDeactivate = (id, name) => {
    toast((t) => (
      <div>
        <p style={{ margin: '0 0 10px', fontWeight: 900, color: UI.colors.text }}>Deactivate {name}?</p>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => { toast.dismiss(t.id); doDeactivate(id, name); }}
            style={{ ...buttonStyle('danger'), padding: '8px 12px', borderRadius: 12 }}>Deactivate</button>
          <button onClick={() => toast.dismiss(t.id)}
            style={{ ...buttonStyle('outline'), padding: '8px 12px', borderRadius: 12 }}>Cancel</button>
        </div>
      </div>
    ), { duration: Infinity });
  };

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return agents.filter((a) =>
      (a.name || '').toLowerCase().includes(q) ||
      (a.email || '').toLowerCase().includes(q) ||
      (a.phone || '').toLowerCase().includes(q)
    );
  }, [agents, search]);

  const stats = useMemo(() => ({
    total: agents.length,
    active: agents.filter((a) => a.is_active).length,
    avgRate: agents.length
      ? (agents.reduce((s, a) => s + parseFloat(a.commission_rate ?? 10), 0) / agents.length).toFixed(1)
      : '0.0',
  }), [agents]);

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 260 }}>
        <div style={{ width: 36, height: 36, border: `3px solid ${UI.colors.border}`, borderTopColor: UI.colors.primary, borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {showModal && (
        <CreateAgentModal
          onClose={() => setShowModal(false)}
          onCreated={(agent) => setAgents((prev) => [agent, ...prev])}
        />
      )}

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ fontFamily: UI.font.heading, fontSize: 30, fontWeight: 900, margin: 0, color: UI.colors.text }}>Manage Agents</h1>
          <p style={{ margin: '8px 0 0', fontSize: 14, color: UI.colors.muted }}>{agents.length} agents registered</p>
        </div>
        <button
          type="button"
          onClick={() => setShowModal(true)}
          style={{ ...buttonStyle('primary'), padding: '11px 20px', borderRadius: 14 }}
        >
          <UserPlus size={16} /> Add Agent
        </button>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 14 }}>
        {[
          { label: 'Total Agents', value: stats.total, color: '#7c3aed', bg: 'rgba(124,58,237,0.10)' },
          { label: 'Active Agents', value: stats.active, color: '#16a34a', bg: 'rgba(22,163,74,0.10)' },
          { label: 'Avg. Commission', value: `${stats.avgRate}%`, color: '#ea580c', bg: 'rgba(234,88,12,0.10)' },
        ].map((s) => (
          <div key={s.label} style={{ ...ui.card, padding: 18, textAlign: 'center', backgroundColor: s.bg, borderColor: 'transparent' }}>
            <p style={{ margin: 0, fontFamily: UI.font.heading, fontSize: 28, fontWeight: 900, color: s.color }}>{s.value}</p>
            <p style={{ margin: '6px 0 0', fontSize: 11, fontWeight: 900, color: '#9ca3af', letterSpacing: '0.08em', textTransform: 'uppercase' }}>{s.label}</p>
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
        <div className="input-row" style={{ ...inputRowStyle, flex: 1, minWidth: 240 }}>
          <Search size={16} color="#9ca3af" />
          <input
            type="text"
            placeholder="Search agents by name, email or phone..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={inputCore}
          />
          {search && (
            <button type="button" onClick={() => setSearch('')}
              style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#9ca3af', padding: 2, display: 'flex' }}>
              <X size={14} />
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div style={ui.card}>
        <div style={{ padding: 22 }}>
          {filtered.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '56px 18px', color: UI.colors.muted }}>
              <Users size={44} color="#9ca3af" style={{ opacity: 0.3, margin: '0 auto 12px', display: 'block' }} />
              <p style={{ margin: 0, fontWeight: 900 }}>No agents found</p>
              <p style={{ margin: '8px 0 0', fontSize: 13 }}>
                {search ? 'Try a different search term.' : 'Click "Add Agent" to create the first agent.'}
              </p>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <div style={{ minWidth: 760 }}>
                {/* Table Header */}
                <div style={{
                  display: 'grid', gridTemplateColumns: '2fr 2fr 1.2fr 1.2fr 1fr 1fr',
                  gap: 12, padding: '10px 12px', borderRadius: 14,
                  backgroundColor: '#f3f4f6', border: `1px solid ${UI.colors.border}`,
                  fontSize: 11, fontWeight: 900, letterSpacing: '0.08em',
                  textTransform: 'uppercase', color: '#9ca3af', marginBottom: 10,
                }}>
                  <span>Agent</span>
                  <span>Contact</span>
                  <span>Commission</span>
                  <span>WhatsApp</span>
                  <span>Status</span>
                  <span style={{ textAlign: 'right' }}>Actions</span>
                </div>

                {filtered.map((agent) => (
                  <div
                    key={agent.id}
                    style={{
                      display: 'grid', gridTemplateColumns: '2fr 2fr 1.2fr 1.2fr 1fr 1fr',
                      gap: 12, padding: '14px 12px',
                      borderBottom: `1px solid ${UI.colors.border}`,
                      alignItems: 'center', fontSize: 13,
                    }}
                  >
                    {/* Agent name + avatar */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                      <div style={{ width: 38, height: 38, borderRadius: 9999, backgroundColor: 'rgba(124,58,237,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <span style={{ fontWeight: 900, color: '#7c3aed', fontSize: 15 }}>{(agent.name || '?')[0].toUpperCase()}</span>
                      </div>
                      <div style={{ minWidth: 0 }}>
                        <p style={{ margin: 0, fontWeight: 800, color: UI.colors.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{agent.name}</p>
                        <p style={{ margin: '2px 0 0', fontSize: 11, color: UI.colors.muted }}>Since {new Date(agent.created_at).toLocaleDateString()}</p>
                      </div>
                    </div>

                    {/* Contact */}
                    <div style={{ minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                        <Mail size={12} color="#9ca3af" />
                        <span style={{ fontSize: 12, color: UI.colors.muted, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{agent.email}</span>
                      </div>
                      {agent.phone && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <Phone size={12} color="#9ca3af" />
                          <span style={{ fontSize: 12, color: UI.colors.muted, fontWeight: 700 }}>{agent.phone}</span>
                        </div>
                      )}
                    </div>

                    {/* Commission rate - inline editable */}
                    <CommissionEditor agent={agent} onSaved={handleCommissionSaved} />

                    {/* WhatsApp button (placeholder) */}
                    <button
                      type="button"
                      title="WhatsApp credential sharing — coming soon"
                      onClick={() => toast('WhatsApp integration coming soon!', { icon: '📱' })}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                        padding: '7px 12px', borderRadius: 10, border: '1.5px solid #bbf7d0',
                        backgroundColor: '#f0fdf4', color: '#16a34a', cursor: 'pointer',
                        fontSize: 12, fontWeight: 900,
                      }}
                    >
                      <MessageCircle size={14} />
                      Send
                    </button>

                    {/* Status pill */}
                    <span style={statusPill(Boolean(agent.is_active))}>
                      {agent.is_active ? 'Active' : 'Inactive'}
                    </span>

                    {/* Actions */}
                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                      {agent.is_active && (
                        <button
                          type="button"
                          onClick={() => handleDeactivate(agent.id, agent.name)}
                          disabled={deactivating === agent.id}
                          title="Deactivate agent"
                          style={{
                            ...buttonStyle('outline'),
                            padding: '8px 10px', borderRadius: 12,
                            borderColor: '#fecaca', color: UI.colors.danger,
                            opacity: deactivating === agent.id ? 0.75 : 1,
                          }}
                        >
                          {deactivating === agent.id
                            ? <span style={{ width: 12, height: 12, border: '2px solid rgba(255,255,255,0.35)', borderTopColor: UI.colors.danger, borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
                            : <UserX size={15} />}
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ManageAgents;
